import { IBlogPostDto, TBlogCard } from '@blog/types';
import { PaginationProps, PaginationReturn } from '@common/types/service';
import { OverviewBanner } from '@contentManagement/types';
import { OverviewNews, OverviewVideo } from '@overview/types/overview';
import { api } from '@services/api';
import { getPaginationString } from '@utils/service';
import he from 'he';

const overviewApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getOverviewBlogItems: builder.query<
            TBlogCard[],
            { per_page?: number; page?: number; search?: string; categories?: number; orderby?: string }
        >({
            query: ({ page = 1, per_page = 6, search, categories, orderby }) => {
                return {
                    url: 'overview/blog',
                    params: { page, per_page, search, categories, orderby, _embed: true },
                };
            },
            transformResponse: (response: { posts: IBlogPostDto[] }) =>
                response.posts.map((post) => ({
                    ...post,
                    title: he.decode(post.title.rendered),
                    excerpt: post.excerpt_plain_text,
                    author: post._embedded.author.map((auth) => auth.name),
                    image: post._embedded['wp:featuredmedia']
                        ? {
                              url: post._embedded['wp:featuredmedia'][0].media_details?.sizes?.['blogpost-list']
                                  ?.source_url,
                              alt_text: post._embedded['wp:featuredmedia'][0].alt_text,
                          }
                        : undefined,
                })),
            providesTags: ['OverviewBlog'],
        }),
        getOverviewNews: builder.query<PaginationReturn<OverviewNews>, PaginationProps>({
            query: ({ filters, ...pagination }) =>
                `overview/news${pagination ? `?${getPaginationString(pagination)}` : ''}`,
            providesTags: ['OverviewNews'],
        }),
        getOverviewVideos: builder.query<
            PaginationReturn<OverviewVideo, OverviewVideo['external_id']>,
            PaginationProps
        >({
            query: ({ filters, ...pagination }) =>
                `overview/video${pagination ? `?${getPaginationString(pagination)}` : ''}`,
            providesTags: ['OverviewVideos'],
        }),
        getOverviewBanner: builder.query<OverviewBanner, void>({
            query: () => 'overview/banner',
            providesTags: ['OverviewBanner'],
        }),
    }),
});

export const {
    useGetOverviewBlogItemsQuery,
    useGetOverviewNewsQuery,
    useGetOverviewVideosQuery,
    useGetOverviewBannerQuery,
    usePrefetch,
} = overviewApi;
